import { RefundFormValues } from "@/hooks/use-refund-form";
import { UserLocation } from "@/hooks/use-location-handling";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, DollarSign, FileText } from "lucide-react";
import { usePlatformIntelligence } from "@/hooks/use-platform-intelligence";
import PlatformIntelligenceCard from "./PlatformIntelligenceCard";

interface AdditionalDetailsStepProps {
  values: RefundFormValues;
  errors: { [key: string]: string };
  onChange: (field: keyof RefundFormValues, value: string) => void;
  userLocation: UserLocation;
  isLoadingLocation: boolean;
}

const AdditionalDetailsStep = ({ values, errors, onChange, userLocation, isLoadingLocation }: AdditionalDetailsStepProps) => {
  const { platformInfo, strategy, recommendations, isAnalyzed } = usePlatformIntelligence(values, userLocation);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">Additional Details</h2>
        <p className="text-muted-foreground">
          Provide transaction details to complete your refund request
        </p>
      </div>

      {/* Platform Intelligence Card */}
      {isAnalyzed && platformInfo && strategy && recommendations && (
        <PlatformIntelligenceCard 
          platformInfo={platformInfo}
          strategy={strategy}
          recommendations={recommendations}
        />
      )}

      {/* Amount and Currency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="amount" className="text-sm font-medium">
              Amount *
            </Label>
          </div>
          <Input
            id="amount"
            placeholder="e.g., 89.99"
            type="number"
            value={values.amount}
            onChange={(e) => onChange('amount', e.target.value)}
            className={errors.amount ? "border-destructive" : ""}
          />
          {errors.amount && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {errors.amount}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Label htmlFor="currency" className="text-sm font-medium">
            Currency
          </Label>
          <Select onValueChange={(value) => onChange('currency', value)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="USD" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              {/* Add more currencies as needed */}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Order Information */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="orderNumber" className="text-sm font-medium">
            Order Number
          </Label>
        </div>
        <Input
          id="orderNumber"
          placeholder="e.g., 123456789"
          value={values.orderNumber}
          onChange={(e) => onChange('orderNumber', e.target.value)}
        />
      </div>
    </div>
  );
};

export default AdditionalDetailsStep;
